// The catalogue-charter REGISTRY — the single source for the Contract
// surface's field vocabulary (ADR-060 §2): which contractual fields exist,
// which natures each may legally take (the allowed-natures matrix), and the
// shared derivations both sides consume (effective charter · config
// completeness · band/none pairing). The editor's pills, the backend's
// guards, and the DB CHECK on catalogue_charter all mirror THIS module —
// one home, so frontend and backend can never disagree about what's legal.
import { z } from 'zod';
// ── natures ──────────────────────────────────────────────────────────────────
// The charter is contract-side ONLY — a nature is a fact of the CONTRACT.
// PRICED — varies the price; enters matching; deviation moves the invoice.
// COMMITMENT — contractually binding on the ESCO, whatever its origin (a value
// the ESCO itself proposed in a signed technical annex binds exactly as a
// customer's bar does); a gap is a breach on its own clock.
// OFF — this contract doesn't speak of it (the default; never stored — an
// absent catalogue_charter row IS off).
// The ESCO's UNSIGNED engineering targets are NOT charter material (they
// project against the installed solution per site — the future design register).
export const CHARTER_NATURES = ['priced', 'commitment', 'off'];
export const CharterNatureSchema = z.enum(CHARTER_NATURES);
// ── band families (band CRUD + tolerance route vocabulary) ──────────────────
export const BAND_FAMILIES = ['load', 'grid_availability', 'solar', 'cooling'];
export const BandFamilySchema = z.enum(BAND_FAMILIES);
// ── the fields ───────────────────────────────────────────────────────────────
export const CHARTER_FIELDS = [
    // customer designations (site_type / priority / category = M2M validity
    // sets on configs; environment = a 2-value config column)
    'environment',
    'site_type',
    'priority',
    'category',
    // bands (load = the one locked always-Priced axis)
    'load_band',
    'grid_availability_band',
    'solar_band',
    'cooling_band',
    // composition ({source}_required tri-state config columns)
    'grid_required',
    'solar_required',
    'generator_required',
    'cooling_required',
    'wind_required',
    // ESCO scalars (the committed floor IS the min)
    'power_system_capacity_min_kw',
    'battery_capacity_min_ah',
    'battery_chemistry',
    'generator_capacity_min_kva',
    'generator_units_min',
    'grid_capacity_min_kw',
    'cooling_units_min',
    'wind_capacity_min_kw',
    // performance KPIs (direction in the name · period in the unit)
    'battery_autonomy_min_h',
    'rer_min_pct',
    'solar_production_min_kwh_per_year',
    'diesel_max_l_per_year',
    'grid_import_max_kwh_per_year',
    'runtime_max_h_per_day',
    'cooling_energy_max_kwh_per_year',
    'co2_energy_max_kg_per_year',
    'co2_lca_max_kg_per_year',
];
export const CharterFieldSchema = z.enum(CHARTER_FIELDS);
const PRICED_OR_OFF = ['priced', 'off'];
const PRICED_OR_COMMITTED = ['priced', 'commitment', 'off'];
const COMMITTED_OR_OFF = ['commitment', 'off'];
export const CHARTER_FIELD_SPECS = {
    // Designations: priced | off — never a commitment (the customer's own
    // designations are not the ESCO's deliverable), nothing to document.
    environment: { group: 'designation', allowedNatures: PRICED_OR_OFF },
    site_type: { group: 'designation', allowedNatures: PRICED_OR_OFF },
    priority: { group: 'designation', allowedNatures: PRICED_OR_OFF },
    category: { group: 'designation', allowedNatures: PRICED_OR_OFF },
    // load: every energy price has a demand basis — locked Priced.
    load_band: { group: 'band', allowedNatures: ['priced'], locked: true },
    // Bands are their OWN axis: the moment a source is in the contract its size
    // tiers may be off, a pricing driver, or a contract commitment (outside the
    // window = a breach on its own clock, not a price event). Presence stays the
    // master, and the band's role is CAPPED AT THE PRESENCE'S price-rank (ADR-060 §2,
    // charterStateViolations): an off source carries no band; a COMMITMENT
    // presence's band can only be a commitment — a priced band's "none"
    // coordinate is a price address, which a must-have (committed) presence
    // forbids, so a band can never out-price its presence. This per-field matrix
    // stays the static ceiling (priced | commitment | off); the presence cap is
    // the cross-field rule.
    grid_availability_band: { group: 'band', allowedNatures: PRICED_OR_COMMITTED },
    solar_band: { group: 'band', allowedNatures: PRICED_OR_COMMITTED },
    cooling_band: { group: 'band', allowedNatures: PRICED_OR_COMMITTED },
    // Composition: presence is imposable ("every site must have a DG") or a
    // priced coordinate; the *_required name serves both natures.
    grid_required: { group: 'composition', allowedNatures: PRICED_OR_COMMITTED },
    solar_required: { group: 'composition', allowedNatures: PRICED_OR_COMMITTED },
    generator_required: { group: 'composition', allowedNatures: PRICED_OR_COMMITTED },
    cooling_required: { group: 'composition', allowedNatures: PRICED_OR_COMMITTED },
    wind_required: { group: 'composition', allowedNatures: PRICED_OR_COMMITTED },
    // ESCO scalars: never priced (moral-hazard rule — operational results and
    // the ESCO's own sizing never price; a contract pricing by one = "create a
    // BAND that day"). Committed when the signed text imposes them, else off.
    power_system_capacity_min_kw: { group: 'scalar', allowedNatures: COMMITTED_OR_OFF },
    battery_capacity_min_ah: { group: 'scalar', allowedNatures: COMMITTED_OR_OFF },
    battery_chemistry: { group: 'scalar', allowedNatures: COMMITTED_OR_OFF },
    generator_capacity_min_kva: { group: 'scalar', allowedNatures: COMMITTED_OR_OFF },
    generator_units_min: { group: 'scalar', allowedNatures: COMMITTED_OR_OFF },
    grid_capacity_min_kw: { group: 'scalar', allowedNatures: COMMITTED_OR_OFF },
    cooling_units_min: { group: 'scalar', allowedNatures: COMMITTED_OR_OFF },
    wind_capacity_min_kw: { group: 'scalar', allowedNatures: COMMITTED_OR_OFF },
    // Performance KPIs: measured downstream; binary vs the contract's own value
    // at the year true-up — never priced.
    battery_autonomy_min_h: { group: 'kpi', allowedNatures: COMMITTED_OR_OFF },
    rer_min_pct: { group: 'kpi', allowedNatures: COMMITTED_OR_OFF },
    solar_production_min_kwh_per_year: { group: 'kpi', allowedNatures: COMMITTED_OR_OFF },
    diesel_max_l_per_year: { group: 'kpi', allowedNatures: COMMITTED_OR_OFF },
    grid_import_max_kwh_per_year: { group: 'kpi', allowedNatures: COMMITTED_OR_OFF },
    runtime_max_h_per_day: { group: 'kpi', allowedNatures: COMMITTED_OR_OFF },
    cooling_energy_max_kwh_per_year: { group: 'kpi', allowedNatures: COMMITTED_OR_OFF },
    co2_energy_max_kg_per_year: { group: 'kpi', allowedNatures: COMMITTED_OR_OFF },
    co2_lca_max_kg_per_year: { group: 'kpi', allowedNatures: COMMITTED_OR_OFF },
};
/** Storable fields = everything except the locked load_band (which is never a
 *  row — and 'off' is never stored either: absent = off). Mirrors the DB
 *  CHECK on catalogue_charter.field. */
export const STORABLE_CHARTER_FIELDS = CHARTER_FIELDS.filter((f) => !CHARTER_FIELD_SPECS[f].locked);
/** The config column(s) each charter field governs — the service rejects
 *  writes to un-engaged fields (law 1) and the editor renders per engagement.
 *  Designations map to their validity arrays (wire names). */
export const CHARTER_FIELD_COLUMNS = {
    environment: ['environment'],
    site_type: ['site_type_ids'],
    priority: ['priority_ids'],
    category: ['category_ids'],
    load_band: ['load_band_id'],
    grid_availability_band: ['grid_availability_band_id', 'grid_availability_band_none'],
    solar_band: ['solar_band_id', 'solar_band_none'],
    cooling_band: ['cooling_band_id', 'cooling_band_none'],
    grid_required: ['grid_required'],
    solar_required: ['solar_required'],
    generator_required: ['generator_required'],
    cooling_required: ['cooling_required'],
    wind_required: ['wind_required'],
    power_system_capacity_min_kw: ['power_system_capacity_min_kw'],
    battery_capacity_min_ah: ['battery_capacity_min_ah'],
    battery_chemistry: ['battery_chemistry'],
    generator_capacity_min_kva: ['generator_capacity_min_kva'],
    generator_units_min: ['generator_units_min'],
    grid_capacity_min_kw: ['grid_capacity_min_kw'],
    cooling_units_min: ['cooling_units_min'],
    wind_capacity_min_kw: ['wind_capacity_min_kw'],
    battery_autonomy_min_h: ['battery_autonomy_min_h'],
    rer_min_pct: ['rer_min_pct'],
    solar_production_min_kwh_per_year: ['solar_production_min_kwh_per_year'],
    diesel_max_l_per_year: ['diesel_max_l_per_year'],
    grid_import_max_kwh_per_year: ['grid_import_max_kwh_per_year'],
    runtime_max_h_per_day: ['runtime_max_h_per_day'],
    cooling_energy_max_kwh_per_year: ['cooling_energy_max_kwh_per_year'],
    co2_energy_max_kg_per_year: ['co2_energy_max_kg_per_year'],
    co2_lca_max_kg_per_year: ['co2_lca_max_kg_per_year'],
};
/** The three banded sources whose configs carry a (band FK, none-mark) pair +
 *  the composition flag each must AGREE with (required=true ⟷ never none;
 *  required=false ⟷ never a band value). Load is deliberately absent: every
 *  site has a load — no none coordinate exists for it. */
export const BANDED_SOURCES = [
    { bandField: 'grid_availability_band', requiredField: 'grid_required', family: 'grid_availability' },
    { bandField: 'solar_band', requiredField: 'solar_required', family: 'solar' },
    { bandField: 'cooling_band', requiredField: 'cooling_required', family: 'cooling' },
];
export const CHARTER_SOURCES = [
    { key: 'grid', requiredField: 'grid_required', bandField: 'grid_availability_band', family: 'grid_availability', valueFields: ['grid_capacity_min_kw', 'grid_import_max_kwh_per_year'] },
    { key: 'solar', requiredField: 'solar_required', bandField: 'solar_band', family: 'solar', valueFields: ['solar_production_min_kwh_per_year'], renewable: true },
    { key: 'generator', requiredField: 'generator_required', valueFields: ['generator_capacity_min_kva', 'generator_units_min', 'runtime_max_h_per_day', 'diesel_max_l_per_year'] },
    { key: 'cooling', requiredField: 'cooling_required', bandField: 'cooling_band', family: 'cooling', valueFields: ['cooling_units_min', 'cooling_energy_max_kwh_per_year'] },
    { key: 'wind', requiredField: 'wind_required', valueFields: ['wind_capacity_min_kw'], renewable: true },
];
/** The charter band field owning each family (load's is the locked Priced one).
 *  Registry-owned because BOTH sides project through it — the surfaces to pick
 *  a column's nature, the import/export column-model to decide whether the
 *  family's column exists at all. */
export const BAND_FAMILY_FIELD = {
    load: 'load_band',
    grid_availability: 'grid_availability_band',
    solar: 'solar_band',
    cooling: 'cooling_band',
};
/** The config row's (band FK, none-mark) column pair per family — load has no
 *  none coordinate (every site has a load, so absence is not a coordinate). */
export const BAND_FAMILY_COLUMNS = {
    load: { id: 'load_band_id', none: null },
    grid_availability: { id: 'grid_availability_band_id', none: 'grid_availability_band_none' },
    solar: { id: 'solar_band_id', none: 'solar_band_none' },
    cooling: { id: 'cooling_band_id', none: 'cooling_band_none' },
};
/** The core kit every site has — value terms with no source dependency, in
 *  display order (the power SYSTEM first, then the battery trio). Together with
 *  each source's `valueFields` and ENV_FIELDS this homes every scalar/KPI term
 *  exactly once; the surfaces and the import/export column set read the SAME
 *  order, so a file column can never sit somewhere the peek doesn't. */
export const CORE_FIELDS = [
    'power_system_capacity_min_kw',
    'battery_chemistry',
    'battery_capacity_min_ah',
    'battery_autonomy_min_h',
];
/** Outcome metrics, in display order. RER additionally depends on a renewable
 *  source being engaged (see renewablesEngaged); the CO₂ ceilings don't. */
export const ENV_FIELDS = [
    'rer_min_pct',
    'co2_energy_max_kg_per_year',
    'co2_lca_max_kg_per_year',
];
/** "Is a renewable source in the contract?" — legitimizes the renewable-share
 *  KPI (rer_min_pct); the last renewable leaving takes it along. */
export function renewablesEngaged(natures) {
    return CHARTER_SOURCES.some((s) => s.renewable &&
        (natures[s.requiredField] !== 'off' || (s.bandField !== undefined && natures[s.bandField] !== 'off')));
}
/** The WHOLE-charter cross-field laws — judged on the effective post-state by
 *  every writer (the service on both verbs; the drawers stage to the same
 *  rules, so a violation only ever reaches a raw API caller):
 *  1. presence is the master — an OFF source carries no band and no value terms;
 *  2. a band never out-prices its presence — a COMMITMENT presence's
 *     band can only be a commitment (off < commitment < priced): a priced
 *     band's "none" coordinate is a price address a must-have presence forbids;
 *  3. the renewable-share KPI needs a renewable source engaged. */
export function charterStateViolations(natures) {
    const out = [];
    for (const src of CHARTER_SOURCES) {
        const presence = natures[src.requiredField];
        if (presence === 'off') {
            if (src.bandField && natures[src.bandField] !== 'off') {
                out.push({ code: 'SOURCE_OFF_DEPENDENTS', field: src.bandField, source: src.requiredField });
            }
            for (const f of src.valueFields) {
                if (natures[f] !== 'off')
                    out.push({ code: 'SOURCE_OFF_DEPENDENTS', field: f, source: src.requiredField });
            }
        }
        else if (presence === 'commitment' && src.bandField && natures[src.bandField] === 'priced') {
            out.push({ code: 'BAND_OUTPRICES_PRESENCE', field: src.bandField, source: src.requiredField });
        }
    }
    if (natures.rer_min_pct !== 'off' && !renewablesEngaged(natures)) {
        out.push({ code: 'RER_NO_RENEWABLE', field: 'rer_min_pct' });
    }
    return out;
}
/** Compose the effective charter: stored non-off rows over the ruled defaults
 *  (everything Off except the locked load_band = Priced). Unknown stored
 *  fields are ignored (forward-compat: a removed field's rows die at reset). */
export function effectiveCharter(rows) {
    const out = Object.fromEntries(CHARTER_FIELDS.map((f) => [f, CHARTER_FIELD_SPECS[f].locked ? 'priced' : 'off']));
    for (const row of rows) {
        const parsed = CharterFieldSchema.safeParse(row.field);
        const nature = CharterNatureSchema.safeParse(row.nature);
        if (parsed.success && nature.success && nature.data !== 'off' && !CHARTER_FIELD_SPECS[parsed.data].locked) {
            out[parsed.data] = nature.data;
        }
    }
    return out;
}
/** Law 2 (null follows nature), the Priced half: every Priced field needs an
 *  answer on every config — none counts, a validity set needs ≥ 1 row, an
 *  unfilled Priced field = the config is INCOMPLETE (derived, never stored —
 *  ADR-061). Scalars/KPIs never appear here: they can't be Priced (matrix),
 *  and under Commitment null is a legal statement (no imposition on this
 *  config). */
export function configIncompleteFields(cfg, charter) {
    const missing = [];
    const need = (field, answered) => {
        if (charter[field] === 'priced' && !answered)
            missing.push(field);
    };
    need('environment', cfg.environment != null);
    need('site_type', cfg.site_type_ids.length > 0);
    need('priority', cfg.priority_ids.length > 0);
    need('category', cfg.category_ids.length > 0);
    need('load_band', cfg.load_band_id != null);
    need('grid_availability_band', cfg.grid_availability_band_id != null || cfg.grid_availability_band_none);
    need('solar_band', cfg.solar_band_id != null || cfg.solar_band_none);
    need('cooling_band', cfg.cooling_band_id != null || cfg.cooling_band_none);
    need('grid_required', cfg.grid_required != null);
    need('solar_required', cfg.solar_required != null);
    need('generator_required', cfg.generator_required != null);
    need('cooling_required', cfg.cooling_required != null);
    need('wind_required', cfg.wind_required != null);
    return missing;
}
/** The flag/band agreement guard (ADR-060 §2: "bands go none exactly where the
 *  cell's presence is absent"): required=true with a none-mark, or
 *  required=false with a band value, is a contradiction — blocked wherever
 *  both fields are engaged. Returns the offending band fields.
 *
 *  ENGAGED coordinates only: a disengaged band axis's stored values are
 *  legal-unused (the same stance law 1 takes on later-Off fields) — judging
 *  them would brick every legacy row, because law 1 forbids the client from
 *  writing (and so from cleaning) an un-engaged column. When the axis
 *  re-engages, the form's cascade heals the pair on the next save. */
export function configBandConflicts(cfg, charter) {
    const out = [];
    for (const src of BANDED_SOURCES) {
        if (charter[src.bandField] === 'off')
            continue;
        const none = cfg[`${src.bandField}_none`];
        const bandId = cfg[`${src.bandField}_id`];
        const required = cfg[src.requiredField];
        if (required === true && none)
            out.push(src.bandField);
        if (required === false && bandId != null)
            out.push(src.bandField);
    }
    return out;
}
